/**
 * Workout-frequency calendar: the pure data side.
 *
 * Buckets workouts into local calendar days and lays those days out as the
 * week-per-column grid the view renders. No DOM, so the whole layout is
 * testable without a browser.
 *
 * Unlike the stat cards, this counts every exercise together: the question it
 * answers is "did I work out that day", not "how did I do at push-ups".
 */
import type { Workout, WorkoutData } from '../types';

/** Workouts logged on one local day, with the per-exercise split for tooltips. */
export interface DaySummary {
  count: number;
  byExercise: Record<string, number>;
}

/** Keyed by `localDayKey`. */
export type DayIndex = Map<string, DaySummary>;

/** 0 is "nothing logged"; 1-4 are the shading steps. */
export type IntensityLevel = 0 | 1 | 2 | 3 | 4;

export interface CalendarCell {
  /** Local day key, or null for the padding that squares off the end weeks. */
  key: string | null;
  /** Local midnight on that day; null for padding. */
  date: Date | null;
  count: number;
  level: IntensityLevel;
  /** A day that has not happened yet: rendered blank, never as "0 workouts". */
  future: boolean;
  isToday: boolean;
}

export interface MonthLabel {
  /** Zero-based week column this label sits above. */
  column: number;
  label: string;
}

export interface CalendarGrid {
  year: number;
  /** One entry per week column; each holds 7 cells, index 0 = Sunday. */
  weeks: CalendarCell[][];
  monthLabels: MonthLabel[];
  /** Workouts logged in this year. */
  total: number;
  /** Days in this year carrying at least one workout. */
  activeDays: number;
}

const MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

/**
 * A day identifier in the *viewer's own* timezone.
 *
 * Deliberately not `toISOString().slice(0, 10)`: that converts to UTC first,
 * so a 9pm workout in Florida would be filed under the following day and the
 * calendar would disagree with the history list sitting right above it.
 */
export function localDayKey(date: Date): string {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Fixed thresholds rather than a share of the busiest day.
 *
 * Scaling to the maximum means one unusual six-workout day flattens every
 * ordinary day to the palest shade, so a consistent month reads as an empty
 * one. Absolute steps keep a square's colour meaning the same thing all year.
 */
export function intensityLevel(count: number): IntensityLevel {
  if (count <= 0) return 0;
  if (count >= 4) return 4;
  return count as 1 | 2 | 3;
}

function eachWorkout(data: WorkoutData): Workout[] {
  return Object.values(data).flatMap((list) => list ?? []);
}

/** Counts workouts per local day, across every exercise. */
export function countWorkoutsByDay(data: WorkoutData): DayIndex {
  const index: DayIndex = new Map();

  for (const workout of eachWorkout(data)) {
    const when = new Date(workout.date);
    // Corrupt or hand-edited storage should not blank the whole calendar.
    if (Number.isNaN(when.getTime())) continue;

    const key = localDayKey(when);
    const summary = index.get(key) ?? { count: 0, byExercise: {} };
    summary.count += 1;
    summary.byExercise[workout.exercise] = (summary.byExercise[workout.exercise] ?? 0) + 1;
    index.set(key, summary);
  }

  return index;
}

/**
 * Years holding workouts, newest first, always including the current year so a
 * brand new user sees an empty calendar rather than an empty dropdown.
 */
export function workoutYears(data: WorkoutData, today: Date = new Date()): number[] {
  const years = new Set<number>([today.getFullYear()]);

  for (const workout of eachWorkout(data)) {
    const when = new Date(workout.date);
    if (Number.isNaN(when.getTime())) continue;
    years.add(when.getFullYear());
  }

  return [...years].sort((a, b) => b - a);
}

/**
 * Lays one year out as whole weeks, Sunday-first, padded at both ends so every
 * column holds seven cells.
 *
 * Dates advance by incrementing the day field of a local Date, which lands on
 * local midnight every time regardless of month length or daylight saving.
 *
 * Adding 86_400_000 ms instead picks up an hour of drift at each DST
 * transition. In America/New_York the two transitions cancel within a year, so
 * the day *count* still comes out right and the bug hides -- but the cursor
 * sits an hour off midnight for two thirds of the year, and in a zone whose
 * transition falls at or near midnight (Havana, Santiago, Tehran, or Lord
 * Howe's half-hour shift) that hour moves a square onto the wrong date. Hence
 * the invariant the tests assert: every cell is local midnight.
 */
export function buildCalendarGrid(
  year: number,
  index: DayIndex,
  today: Date = new Date(),
): CalendarGrid {
  const todayKey = localDayKey(today);
  const yearStart = new Date(year, 0, 1);
  const yearEnd = new Date(year, 11, 31);

  const cursor = new Date(year, 0, 1 - yearStart.getDay());
  const lastCell = new Date(year, 11, 31 + (6 - yearEnd.getDay()));

  const weeks: CalendarCell[][] = [];
  const monthLabels: MonthLabel[] = [];
  let total = 0;
  let activeDays = 0;
  let labelledMonth = -1;

  while (cursor <= lastCell) {
    const week: CalendarCell[] = [];

    for (let weekday = 0; weekday < 7; weekday++) {
      if (cursor.getFullYear() !== year) {
        week.push({ key: null, date: null, count: 0, level: 0, future: false, isToday: false });
      } else {
        const key = localDayKey(cursor);
        const count = index.get(key)?.count ?? 0;
        total += count;
        if (count > 0) activeDays += 1;

        week.push({
          key,
          date: new Date(cursor),
          count,
          level: intensityLevel(count),
          // Keys are fixed-width YYYY-MM-DD, so they order lexicographically.
          future: key > todayKey,
          isToday: key === todayKey,
        });

        // A month is labelled above the first column it appears in, which is
        // how GitHub's reads: the name marks where the month starts.
        if (cursor.getMonth() !== labelledMonth) {
          labelledMonth = cursor.getMonth();
          monthLabels.push({ column: weeks.length, label: MONTH_NAMES[labelledMonth] ?? '' });
        }
      }

      cursor.setDate(cursor.getDate() + 1);
    }

    weeks.push(week);
  }

  return { year, weeks, monthLabels, total, activeDays };
}

/** "1 workout" / "3 workouts" -- the count half of a cell's tooltip. */
export function describeCount(count: number): string {
  return count === 1 ? '1 workout' : `${count} workouts`;
}
